import { OPEN_WAITLIST_STATUS_NAMES } from '@vagusx/clients';
import { formatTimeLabel } from '@/components/custom/preferred-days-grid';
import type {
  WaitlistEntry,
  WaitlistEntryRefreshResponse,
  WaitlistPriority,
  WaitlistStatus,
} from '@vagusx/clients';

/** 0=Sunday, as the API sends days. */
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Monday-first order for the day picker. */
export const DAY_PICKER_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

const WEEKDAYS = [1, 2, 3, 4, 5];

export function formatDays(days: readonly number[] | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return 'Any day';
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length === 5 && WEEKDAYS.every((d) => sorted.includes(d))) return 'Weekdays';
  return DAY_PICKER_ORDER.filter((d) => sorted.includes(d))
    .map((d) => DAY_SHORT[d])
    .join(', ');
}

/** "09:00" → "9:00 AM"; "23:59" (the end of the day) → "end of day". */
export function formatClock(time: string): string {
  return time === '23:59' ? 'end of day' : formatTimeLabel(time);
}

export function formatTimeRange(earliest: string | undefined, latest: string | undefined): string {
  if (earliest && latest) return `${formatClock(earliest)} – ${formatClock(latest)}`;
  if (earliest) return `After ${formatClock(earliest)}`;
  if (latest) return `Before ${formatClock(latest)}`;
  return 'Any time';
}

export const PRIORITY_LABEL: Record<WaitlistPriority, string> = {
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

export const STATUS_LABEL: Record<WaitlistStatus, string> = {
  active: 'Active',
  contacted: 'Contacted',
  offered: 'Offered',
  booked: 'Booked',
  expired: 'Expired',
  cancelled: 'Cancelled',
  removed: 'Removed in EHR',
};

export const REMOVAL_REASON_LABEL = {
  no_longer_needed: 'Patient no longer wants it',
  booked_elsewhere: 'Booked an earlier appointment',
  other: 'Other',
} as const;

export function isOpenStatus(status: WaitlistStatus): boolean {
  return OPEN_WAITLIST_STATUS_NAMES.includes(status);
}

// =============================================================================
// EHR actions on one entry (ADR-277)
// =============================================================================

/**
 * "Send to EHR now": a change is waiting to be sent, or the last push failed —
 * and there is something to send. A closed entry that never reached the EHR has
 * nothing to delete there.
 */
export function canSendToEhr(entry: WaitlistEntry): boolean {
  return (
    (entry.ehr.syncStatus === 'pending' || entry.ehr.syncStatus === 'failed') &&
    (isOpenStatus(entry.status) || entry.ehr.waitlistId !== null)
  );
}

/** "Check in EHR": an open entry in the EHR with no change of ours still to send. */
export function canCheckInEhr(entry: WaitlistEntry): boolean {
  return (
    isOpenStatus(entry.status) &&
    entry.ehr.waitlistId !== null &&
    entry.ehr.syncStatus !== 'pending'
  );
}

/**
 * A queued entry whose next attempt is further off than this has been tried and
 * backed off (the shortest backoff is 20 minutes). The margin absorbs clock skew
 * between the browser and the server that stamped "due now".
 */
const BACKED_OFF_AFTER_MS = 2 * 60 * 1000;

export type PushProgress = 'sending' | 'sent' | 'removed' | 'retrying' | 'failed' | 'not_in_ehr';

/** Where a push someone asked for stands, read off the entry it was asked for. */
export function pushProgress(entry: WaitlistEntry, now: number = Date.now()): PushProgress {
  const { syncStatus, nextAttemptAt } = entry.ehr;
  if (syncStatus === 'synced') return entry.status === 'removed' ? 'removed' : 'sent';
  if (syncStatus === 'failed') return 'failed';
  if (syncStatus === 'not_applicable') return 'not_in_ehr';
  return nextAttemptAt && Date.parse(nextAttemptAt) - now > BACKED_OFF_AFTER_MS
    ? 'retrying'
    : 'sending';
}

export interface EhrActionNotice {
  kind: 'success' | 'info' | 'warning' | 'error';
  message: string;
}

/** What to tell staff once a push they asked for has settled. */
export function pushNotice(
  progress: Exclude<PushProgress, 'sending'>,
  entry: WaitlistEntry,
  formatTime: (iso: string) => string
): EhrActionNotice {
  const problem = entry.ehr.error ? `: ${entry.ehr.error}` : '';
  switch (progress) {
    case 'sent':
      return { kind: 'success', message: 'Sent to the EHR' };
    case 'removed':
      return {
        kind: 'warning',
        message: 'The EHR no longer has this entry, so it is marked removed',
      };
    case 'retrying':
      return {
        kind: 'warning',
        message: `Couldn't send it to the EHR${problem}. It will try again at ${formatTime(
          entry.ehr.nextAttemptAt!
        )}.`,
      };
    case 'failed':
      return { kind: 'error', message: `The EHR didn't take it${problem}` };
    case 'not_in_ehr':
      return {
        kind: 'info',
        message: "This practice's EHR keeps no waitlist; the entry stays here only",
      };
  }
}

/** What to tell staff after re-reading one entry from the EHR. */
export const ENTRY_REFRESH_NOTICE: Record<
  WaitlistEntryRefreshResponse['outcome'],
  EhrActionNotice
> = {
  unchanged: { kind: 'success', message: 'The EHR has this entry as it is here' },
  updated: { kind: 'success', message: 'Updated from the EHR' },
  removed: {
    kind: 'warning',
    message: 'The EHR no longer has this entry, so it is marked removed',
  },
  unresolved: {
    kind: 'warning',
    message:
      "The EHR's copy names a provider, location or type that couldn't be matched here; left as it is",
  },
  superseded: {
    kind: 'info',
    message: 'The entry changed while it was being checked. Try again.',
  },
};
